import { useMemo, useState } from "react";
import { Column } from "../../../../core/components/Column";
import { ModalSimple } from "../../../../core/components/Modal";
import { TextComponent } from "../../../../core/components/TextComponent";
import { NotificationFilledBG } from "../../../../core/icons/Notification";
import { CustomButton } from "../../../../core/components/CustomButton";
import { Row } from "../../../../core/components/Row";
import { useActivateNotifications } from "../../../settings/presentation/hook/useActivateNotifications";
import { useDeviceConditions } from "../../../settings/presentation/hook/useDeviceConditions";
import { isMatchMedia } from "../../../../core/constants/constants";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../core/store/store";
import { changePushState } from "../store/OnboardingSlice";

export const PushNotificationModal = ({
	isModalPushNotification,
	setIsModalPushNotification,
	isGuide = false,
}: {
	isModalPushNotification: boolean;
	setIsModalPushNotification: React.Dispatch<React.SetStateAction<boolean>>;
	isGuide?: boolean;
}) => {
	const [isLodingPushNotifications, setIsLodingPushNotifications] = useState<boolean>(false);
	const activateNotifications = useActivateNotifications();
	const { shouldShowModal, statusNotification } = useDeviceConditions();
	const dispatch: AppDispatch = useDispatch();

	const userAgent = navigator.userAgent;
	const isIphone = /iPhone|iPad|iPod/i.test(userAgent);
	const isMobile = /Android|iPhone|iPad|iPod/i.test(userAgent);
	const isDesktop = !isMobile;
	const isDesktopPointer = window.matchMedia("(pointer: fine)").matches;
	const isDesktopLike = isDesktop || isDesktopPointer;
	const isStandaloneApp = window.matchMedia("(display-mode: standalone)").matches;
	const isSafari = userAgent.includes("Safari") && !userAgent.includes("Chrome");
	const isNotificationsEnabled = statusNotification === "enabled";

	const browserMessage = useMemo<React.ReactNode>(() => {
		if (!isGuide) {
			if (isDesktopLike) {
				return (
					<>
						Enable push notifications to get alerts for messages, matches, and likes.<br /><br />
						Click <b>Enable</b> and allow notifications in your browser prompt.
					</>
				);
			}

			if (isSafari && isIphone) {
				if (isStandaloneApp) {
					return (
						<>
							Enable push notifications to get alerts for messages, matches, and likes.<br /><br />
							Tap <b>Enable</b> and allow notifications when your iPhone asks.
						</>
					);
				}

				return (
					<>
						Open this site in Safari on your iPhone.<br /><br />
						Tap the Share icon, then choose "Add to Home Screen".<br /><br />
						Open the app from your home screen and allow notifications when prompted.
					</>
				);
			}

			return (
				<>
					Open this site on your phone browser.<br /><br />
					Use your browser menu and choose "Add to Home Screen".<br /><br />
					Open the app from your home screen and allow notifications when prompted.
				</>
			);
		}

		if (isNotificationsEnabled) {
			if (isDesktop) {
				return (
					<>
						To disable notifications on desktop:<br /><br />
						1. Click the lock icon next to the website address.<br />
						2. Open site settings.<br />
						3. Set Notifications to "Block".
					</>
				);
			}

			return (
				<>
					To disable notifications on mobile, open browser site settings and set notifications to off.
				</>
			);
		}

		if (statusNotification === "disabled") {
			if (isDesktop) {
				return (
					<>
						You denied notifications for this website.<br /><br />
						To enable notifications on desktop:<br /><br />
						1. Click the lock icon next to the website address.<br />
						2. Open site settings.<br />
						3. Set Notifications to "Allow".<br /><br />
						Then refresh this page.
					</>
				);
			}

			return (
				<>
					You denied notifications for this website.<br /><br />
					Open browser site settings and set notifications to allow.
				</>
			);
		}

		return (
			<>
				Enable push notifications to get alerts for messages, matches, and likes.
			</>
		);
	}, [isGuide, isSafari, isIphone, isNotificationsEnabled, statusNotification, isDesktop, isDesktopLike, isStandaloneApp]);

	if (!isGuide && (!isModalPushNotification || !shouldShowModal || isMatchMedia)) return null;
	if (isGuide && !isModalPushNotification) return null;

	const handleEnableClick = async () => {
		try {
			setIsLodingPushNotifications(true);
			await activateNotifications();
			if (typeof Notification !== "undefined") {
				if (Notification.permission === "granted") {
					localStorage.setItem("NOTIFICATION_PERMISSION_STATE", "enabled");
					dispatch(changePushState("enabled"));
				} else if (Notification.permission === "denied") {
					localStorage.setItem("NOTIFICATION_PERMISSION_STATE", "disabled");
					dispatch(changePushState("disabled"));
				}
			}
			setIsModalPushNotification(false);
		} catch (error) {
			console.error("Error enabling notifications:", error);
		} finally {
			setIsLodingPushNotifications(false);
		}
	};

	const getTitle = () => {
		if (isGuide) {
			if (isNotificationsEnabled) return "Disable Notifications";
			if (statusNotification === "disabled") return "Enable Notifications";
			return "Notifications";
		}
		if (isStandaloneApp || isDesktopLike) return "Enable Notifications";
		return "Add to Home Screen";
	};

	const showEnableButton = !isNotificationsEnabled;
	const closeButtonClass = showEnableButton ? "w-1/2" : "w-full";
	const enableButtonClass = showEnableButton ? "w-1/2 text-primary" : "hidden";

	return (
		<ModalSimple
			title={getTitle()}
			icon={<NotificationFilledBG />}
			onClose={() => setIsModalPushNotification(false)}
			actionExecuted={isModalPushNotification}
			variant="secondary"
			enableButton={false}
		>
			<Column className="gap-5">
				<TextComponent variant="h6" className="text-center">
					{browserMessage}
				</TextComponent>
			</Column>
			<Row className="sticky bg-white pt-5 px-1 pb-1 border border-transparent bottom-0 gap-4">
				<CustomButton
					className={closeButtonClass}
					color="white"
					onClick={() => setIsModalPushNotification(false)}
					small
				>
					Close
				</CustomButton>
				<CustomButton
					className={enableButtonClass}
					onClick={handleEnableClick}
					loading={isLodingPushNotifications}
					small
				>
					Enable
				</CustomButton>
			</Row>
		</ModalSimple>
	);
};
